/**
 * Fullscreen camera barcode scanner (EAN/UPC/Code 128) built on html5-qrcode.
 *
 * Works in normal browsers and in the app's React Native WebView. The library is
 * lazy-loaded from the trigger button's data-scanner-lib, texts come from its
 * data-scanner-texts (CameraScannerView::getButtonAttributes).
 *
 * CameraBarcodeScanner.open($button, {
 *   onDetected: function (code) {},  // camera is paused; call resume() or close()
 *   onClose: function () {},
 *   sheet: $element,                 // optional content shown with the paused camera (e.g. a form)
 *   container: $element,             // optional: render inline in this element instead of fullscreen
 * });
 */
const CameraBarcodeScanner = (function () {
  const OVERLAY_ID = 'camera-scanner-overlay';
  const READER_ID = 'camera-scanner-reader';
  // Ignore the same barcode for a moment after resuming, so a code still in view isn't counted twice
  const SAME_CODE_COOLDOWN_MS = 2500;

  let libraryPromise = null;
  let scanner = null;
  let $overlay = null;
  let $sheetHome = null;
  let settings = {};
  let texts = {};
  let isHandlingCode = false;
  let lastCode = null;
  let lastCodeTime = 0;
  let isOpen = false;

  function loadLibrary(src) {
    if (typeof window.Html5Qrcode !== 'undefined') {
      return Promise.resolve();
    }
    if (!libraryPromise) {
      libraryPromise = new Promise(function (resolve, reject) {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = function () {
          resolve();
        };
        script.onerror = function () {
          libraryPromise = null;
          reject(new Error('library'));
        };
        document.head.appendChild(script);
      });
    }
    return libraryPromise;
  }

  function canUseCamera() {
    return (
      window.isSecureContext !== false &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function'
    );
  }

  function buildOverlay() {
    const $el = $(`
      <div id="${OVERLAY_ID}" class="camera-scanner-overlay" role="dialog" aria-modal="true">
        <div class="camera-scanner-header">
          <span class="camera-scanner-title"></span>
          <button type="button" class="camera-scanner-close" aria-label="">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="camera-scanner-body">
          <div id="${READER_ID}" class="camera-scanner-reader"></div>
          <div class="camera-scanner-status"></div>
          <p class="camera-scanner-hint"></p>
        </div>
        <form class="camera-scanner-manual" autocomplete="off">
          <input type="text" inputmode="numeric" class="form-control camera-scanner-manual-input" />
          <button type="submit" class="btn btn-primary camera-scanner-manual-submit"></button>
        </form>
        <div class="camera-scanner-sheet"></div>
      </div>
    `);
    $el.find('.camera-scanner-title').text(texts.title || '');
    $el.find('.camera-scanner-close').attr('aria-label', texts.close || '');
    $el.find('.camera-scanner-hint').text(texts.hint || '');
    $el
      .find('.camera-scanner-manual-input')
      .attr('placeholder', texts.manual_placeholder || '');
    $el.find('.camera-scanner-manual-submit').text(texts.manual_submit || 'OK');
    return $el;
  }

  function setStatus(message, isError) {
    if (!$overlay) return;
    $overlay
      .find('.camera-scanner-status')
      .text(message || '')
      .toggleClass('is-error', !!isError);
  }

  function getErrorMessage(err) {
    const text = String((err && (err.name || err.message)) || err || '');
    if (text.indexOf('library') !== -1) return texts.error_library;
    if (
      text.indexOf('NotAllowed') !== -1 ||
      text.indexOf('Permission') !== -1
    ) {
      return texts.error_permission;
    }
    if (
      text.indexOf('NotFound') !== -1 ||
      text.indexOf('Overconstrained') !== -1 ||
      text.indexOf('DevicesNotFound') !== -1
    ) {
      return texts.error_no_camera;
    }
    return texts.error_general;
  }

  function getFormats() {
    const formats = window.Html5QrcodeSupportedFormats;
    return [
      formats.EAN_13,
      formats.EAN_8,
      formats.UPC_A,
      formats.UPC_E,
      formats.CODE_128,
    ];
  }

  function shouldMirror() {
    if (!scanner) return false;

    let settings = {};
    try {
      settings = scanner.getRunningTrackSettings() || {};
    } catch (e) {}

    // 1. Best signal: facingMode reported by the browser
    if (settings.facingMode) {
      return settings.facingMode === 'user';
    }

    // 2. Fallback: guess from the camera label
    const video = $overlay && $overlay.find('#' + READER_ID + ' video')[0];
    const track =
      video && video.srcObject && video.srcObject.getVideoTracks()[0];
    const label = (track && track.label) || '';
    if (/front|user|facetime/i.test(label)) return true;
    if (/back|rear|environment/i.test(label)) return false;

    // 3. No info (typical for desktop webcams): they're user-facing, so mirror
    return true;
  }

  function applyMirror() {
    if (!$overlay) return;
    $overlay.find('#' + READER_ID).toggleClass('is-mirrored', shouldMirror());
  }

  function startCamera() {
    if (!canUseCamera()) {
      setStatus(texts.error_insecure, true);
      return Promise.resolve();
    }
    setStatus(texts.starting, false);
    return loadLibrary(settings.libSrc)
      .then(function () {
        if (!isOpen) return;
        if (!scanner) {
          scanner = new window.Html5Qrcode(READER_ID, {
            formatsToSupport: getFormats(),
            // Native BarcodeDetector (Android Chrome/WebView) is faster and more reliable for 1D codes
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
            verbose: false,
          });
        }
        return scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: function (width, height) {
              return {
                width: Math.max(50, Math.floor(width * 0.85)),
                height: Math.max(50, Math.floor(Math.min(width, height) * 0.4)),
              };
            },
          },
          onScanSuccess,
          function () {}
        );
      })
      .then(function () {
        if (isOpen) {
          setStatus('', false);
          applyMirror();
        }
      })
      .catch(function (err) {
        setStatus(getErrorMessage(err), true);
      });
  }

  function stopCamera() {
    if (!scanner) {
      return Promise.resolve();
    }
    const current = scanner;
    scanner = null;
    let stopping;
    try {
      stopping = current.isScanning ? current.stop() : Promise.resolve();
    } catch (e) {
      stopping = Promise.resolve();
    }
    return stopping
      .catch(function () {})
      .then(function () {
        try {
          current.clear();
        } catch (e) {}
      });
  }

  function onScanSuccess(decodedText) {
    const code = String(decodedText || '').trim();
    if (!code || isHandlingCode) return;
    const now = Date.now();
    if (code === lastCode && now - lastCodeTime < SAME_CODE_COOLDOWN_MS) return;
    lastCode = code;
    lastCodeTime = now;
    handleCode(code);
  }

  function handleCode(code) {
    isHandlingCode = true;
    if (scanner && scanner.isScanning) {
      try {
        scanner.pause(true);
      } catch (e) {}
    }
    if (navigator.vibrate) {
      try {
        navigator.vibrate(60);
      } catch (e) {}
    }
    if (typeof settings.onDetected === 'function') {
      settings.onDetected(code);
    } else {
      resume();
    }
  }

  function resume() {
    if (!isOpen) return;
    isHandlingCode = false;
    lastCodeTime = Date.now();
    hideSheet();
    if (scanner) {
      try {
        scanner.resume();
      } catch (e) {}
    }
  }

  function showSheet() {
    if (!$overlay || !settings.$sheet) return;
    $overlay.addClass('has-sheet');
    settings.$sheet.removeClass('d-none');
  }

  function hideSheet() {
    if (!$overlay || !settings.$sheet) return;
    $overlay.removeClass('has-sheet');
    settings.$sheet.addClass('d-none');
  }

  function onVisibilityChange() {
    // Release the camera when the app/tab goes to the background, restart when it returns
    if (!isOpen) return;
    if (document.hidden) {
      stopCamera();
    } else if (!scanner) {
      startCamera().then(function () {
        if (isHandlingCode && scanner && scanner.isScanning) {
          try {
            scanner.pause(true);
          } catch (e) {}
        }
      });
    }
  }

  function open($trigger, options) {
    if (isOpen) return;
    $trigger = $($trigger);
    try {
      texts = JSON.parse($trigger.attr('data-scanner-texts') || '{}');
    } catch (e) {
      texts = {};
    }
    settings = {
      libSrc: $trigger.attr('data-scanner-lib'),
      onDetected: options && options.onDetected,
      onClose: options && options.onClose,
      $sheet: options && options.sheet ? $(options.sheet) : null,
      $container: options && options.container ? $(options.container) : null,
    };
    isOpen = true;
    isHandlingCode = false;
    lastCode = null;

    $overlay = buildOverlay();
    if (settings.$sheet) {
      $sheetHome = settings.$sheet.parent();
      settings.$sheet.addClass('d-none');
      $overlay.find('.camera-scanner-sheet').append(settings.$sheet);
    }
    if (settings.$container) {
      $overlay.addClass('camera-scanner-inline');
      settings.$container.empty().append($overlay).removeClass('d-none');
    } else {
      $('body').append($overlay).addClass('camera-scanner-open');
    }

    $overlay.on('click', '.camera-scanner-close', function (e) {
      e.preventDefault();
      close();
    });
    $overlay.on('submit', '.camera-scanner-manual', function (e) {
      e.preventDefault();
      const $input = $(this).find('.camera-scanner-manual-input');
      const code = String($input.val() || '').trim();
      if (!code || isHandlingCode) return;
      $input.val('').trigger('blur');
      handleCode(code);
    });
    $(document).on('visibilitychange.cameraScanner', onVisibilityChange);
    $(window).on('pagehide.cameraScanner', function () {
      stopCamera();
    });

    startCamera();
  }

  function close() {
    if (!isOpen) return Promise.resolve();
    isOpen = false;
    $(document).off('.cameraScanner');
    $(window).off('.cameraScanner');
    return stopCamera().then(function () {
      if (settings.$sheet && $sheetHome && $sheetHome.length) {
        settings.$sheet.addClass('d-none');
        $sheetHome.append(settings.$sheet);
      }
      if ($overlay) {
        $overlay.remove();
        $overlay = null;
      }
      if (settings.$container) {
        settings.$container.addClass('d-none');
      } else {
        $('body').removeClass('camera-scanner-open');
      }
      if (typeof settings.onClose === 'function') {
        settings.onClose();
      }
    });
  }

  return {
    open: open,
    close: close,
    resume: resume,
    showSheet: showSheet,
    isOpen: function () {
      return isOpen;
    },
  };
})();
